import "core-js/modules/es.array.reduce.js";
import { __values } from "tslib";
import { fromHex, toHex } from "@aws-sdk/util-hex-encoding";
import { Int64 } from "./Int64";
/**
 * @internal
 */

var HeaderMarshaller =
/** @class */
function () {
  function HeaderMarshaller(toUtf8, fromUtf8) {
    this.toUtf8 = toUtf8;
    this.fromUtf8 = fromUtf8;
  }

  HeaderMarshaller.prototype.format = function (headers) {
    var e_1, _a, e_2, _b;

    var chunks = [];

    try {
      for (var _c = __values(Object.keys(headers)), _d = _c.next(); !_d.done; _d = _c.next()) {
        var headerName = _d.value;
        var bytes = this.fromUtf8(headerName);
        chunks.push(Uint8Array.from([bytes.byteLength]), bytes, this.formatHeaderValue(headers[headerName]));
      }
    } catch (e_1_1) {
      e_1 = {
        error: e_1_1
      };
    } finally {
      try {
        if (_d && !_d.done && (_a = _c.return)) _a.call(_c);
      } finally {
        if (e_1) throw e_1.error;
      }
    }

    var out = new Uint8Array(chunks.reduce(function (carry, bytes) {
      return carry + bytes.byteLength;
    }, 0));
    var position = 0;

    try {
      for (var chunks_1 = __values(chunks), chunks_1_1 = chunks_1.next(); !chunks_1_1.done; chunks_1_1 = chunks_1.next()) {
        var chunk = chunks_1_1.value;
        out.set(chunk, position);
        position += chunk.byteLength;
      }
    } catch (e_2_1) {
      e_2 = {
        error: e_2_1
      };
    } finally {
      try {
        if (chunks_1_1 && !chunks_1_1.done && (_b = chunks_1.return)) _b.call(chunks_1);
      } finally {
        if (e_2) throw e_2.error;
      }
    }

    return out;
  };

  HeaderMarshaller.prototype.formatHeaderValue = function (header) {
    switch (header.type) {
      case "boolean":
        return Uint8Array.from([header.value ? 0
        /* boolTrue */
        : 1
        /* boolFalse */
        ]);

      case "byte":
        return Uint8Array.from([2
        /* byte */
        , header.value]);

      case "short":
        var shortView = new DataView(new ArrayBuffer(3));
        shortView.setUint8(0, 3
        /* short */
        );
        shortView.setInt16(1, header.value, false);
        return new Uint8Array(shortView.buffer);

      case "integer":
        var intView = new DataView(new ArrayBuffer(5));
        intView.setUint8(0, 4
        /* integer */
        );
        intView.setInt32(1, header.value, false);
        return new Uint8Array(intView.buffer);

      case "long":
        var longBytes = new Uint8Array(9);
        longBytes[0] = 5
        /* long */
        ;
        longBytes.set(header.value.bytes, 1);
        return longBytes;

      case "binary":
        var binView = new DataView(new ArrayBuffer(3 + header.value.byteLength));
        binView.setUint8(0, 6
        /* byteArray */
        );
        binView.setUint16(1, header.value.byteLength, false);
        var binBytes = new Uint8Array(binView.buffer);
        binBytes.set(header.value, 3);
        return binBytes;

      case "string":
        var utf8Bytes = this.fromUtf8(header.value);
        var strView = new DataView(new ArrayBuffer(3 + utf8Bytes.byteLength));
        strView.setUint8(0, 7
        /* string */
        );
        strView.setUint16(1, utf8Bytes.byteLength, false);
        var strBytes = new Uint8Array(strView.buffer);
        strBytes.set(utf8Bytes, 3);
        return strBytes;

      case "timestamp":
        var tsBytes = new Uint8Array(9);
        tsBytes[0] = 8
        /* timestamp */
        ;
        tsBytes.set(Int64.fromNumber(header.value.valueOf()).bytes, 1);
        return tsBytes;

      case "uuid":
        if (!UUID_PATTERN.test(header.value)) {
          throw new Error("Invalid UUID received: " + header.value);
        }

        var uuidBytes = new Uint8Array(17);
        uuidBytes[0] = 9
        /* uuid */
        ;
        uuidBytes.set(fromHex(header.value.replace(/\-/g, "")), 1);
        return uuidBytes;
    }
  };

  HeaderMarshaller.prototype.parse = function (headers) {
    var out = {};
    var position = 0;

    while (position < headers.byteLength) {
      var nameLength = headers.getUint8(position++);
      var name = this.toUtf8(new Uint8Array(headers.buffer, headers.byteOffset + position, nameLength));
      position += nameLength;

      switch (headers.getUint8(position++)) {
        case 0
        /* boolTrue */
        :
          out[name] = {
            type: BOOLEAN_TAG,
            value: true
          };
          break;

        case 1
        /* boolFalse */
        :
          out[name] = {
            type: BOOLEAN_TAG,
            value: false
          };
          break;

        case 2
        /* byte */
        :
          out[name] = {
            type: BYTE_TAG,
            value: headers.getInt8(position++)
          };
          break;

        case 3
        /* short */
        :
          out[name] = {
            type: SHORT_TAG,
            value: headers.getInt16(position, false)
          };
          position += 2;
          break;

        case 4
        /* integer */
        :
          out[name] = {
            type: INT_TAG,
            value: headers.getInt32(position, false)
          };
          position += 4;
          break;

        case 5
        /* long */
        :
          out[name] = {
            type: LONG_TAG,
            value: new Int64(new Uint8Array(headers.buffer, headers.byteOffset + position, 8))
          };
          position += 8;
          break;

        case 6
        /* byteArray */
        :
          var binaryLength = headers.getUint16(position, false);
          position += 2;
          out[name] = {
            type: BINARY_TAG,
            value: new Uint8Array(headers.buffer, headers.byteOffset + position, binaryLength)
          };
          position += binaryLength;
          break;

        case 7
        /* string */
        :
          var stringLength = headers.getUint16(position, false);
          position += 2;
          out[name] = {
            type: STRING_TAG,
            value: this.toUtf8(new Uint8Array(headers.buffer, headers.byteOffset + position, stringLength))
          };
          position += stringLength;
          break;

        case 8
        /* timestamp */
        :
          out[name] = {
            type: TIMESTAMP_TAG,
            value: new Date(new Int64(new Uint8Array(headers.buffer, headers.byteOffset + position, 8)).valueOf())
          };
          position += 8;
          break;

        case 9
        /* uuid */
        :
          var uuidBytes = new Uint8Array(headers.buffer, headers.byteOffset + position, 16);
          position += 16;
          out[name] = {
            type: UUID_TAG,
            value: toHex(uuidBytes.subarray(0, 4)) + "-" + toHex(uuidBytes.subarray(4, 6)) + "-" + toHex(uuidBytes.subarray(6, 8)) + "-" + toHex(uuidBytes.subarray(8, 10)) + "-" + toHex(uuidBytes.subarray(10))
          };
          break;

        default:
          throw new Error("Unrecognized header type tag");
      }
    }

    return out;
  };

  return HeaderMarshaller;
}();

export { HeaderMarshaller };
var HEADER_VALUE_TYPE;

(function (HEADER_VALUE_TYPE) {
  HEADER_VALUE_TYPE[HEADER_VALUE_TYPE["boolTrue"] = 0] = "boolTrue";
  HEADER_VALUE_TYPE[HEADER_VALUE_TYPE["boolFalse"] = 1] = "boolFalse";
  HEADER_VALUE_TYPE[HEADER_VALUE_TYPE["byte"] = 2] = "byte";
  HEADER_VALUE_TYPE[HEADER_VALUE_TYPE["short"] = 3] = "short";
  HEADER_VALUE_TYPE[HEADER_VALUE_TYPE["integer"] = 4] = "integer";
  HEADER_VALUE_TYPE[HEADER_VALUE_TYPE["long"] = 5] = "long";
  HEADER_VALUE_TYPE[HEADER_VALUE_TYPE["byteArray"] = 6] = "byteArray";
  HEADER_VALUE_TYPE[HEADER_VALUE_TYPE["string"] = 7] = "string";
  HEADER_VALUE_TYPE[HEADER_VALUE_TYPE["timestamp"] = 8] = "timestamp";
  HEADER_VALUE_TYPE[HEADER_VALUE_TYPE["uuid"] = 9] = "uuid";
})(HEADER_VALUE_TYPE || (HEADER_VALUE_TYPE = {}));

var BOOLEAN_TAG = "boolean";
var BYTE_TAG = "byte";
var SHORT_TAG = "short";
var INT_TAG = "integer";
var LONG_TAG = "long";
var BINARY_TAG = "binary";
var STRING_TAG = "string";
var TIMESTAMP_TAG = "timestamp";
var UUID_TAG = "uuid";
var UUID_PATTERN = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/;